import React, { useEffect, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { observer } from 'mobx-react-lite';
import { Spinner, ToastManager as IMToastManager, ThemeContext } from '@insurancemenu/imenu-components-lib';
import { refreshToken } from 'api/refreshToken';
import { useRootStore } from 'store/rootStore';
import { createThemeContext } from '@insurancemenu/imenu-components-lib/dist/hooks/useTheme';
import { eeRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { toJS } from 'mobx';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';
import BrokerApp from './BrokerApp';
import EmployeeApp from './EmployeeApp';
import { SpinnerWrapperStyled } from './globalStyles';

/**
 * Entry shell: loads shared user data, then mounts BrokerApp or EmployeeApp by role.
 */

const App: React.FC = observer(() => {
  const {
    domainStoreV2: { domainHeader },
    userStore: { getUserData, userRoles, isLoading: userLoading },
  } = useRootStore();

  const convertedUserRoles = useMemo(() => userRoles.slice().map((role) => toJS(role)), [userRoles]);

  useEffect(() => {
    // TypeScript doesn't support the cookieStore API yet, so we need to use a type assertion
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const cookieStore = (
      window as {
        cookieStore?: {
          addEventListener: (event: string, handler: (e: { changed?: Array<{ name: string }> }) => void) => void;
          removeEventListener: (event: string, handler: (e: { changed?: Array<{ name: string }> }) => void) => void;
        };
      }
    ).cookieStore;

    const onCookieChange = (e: { changed?: Array<{ name: string }> }): void => {
      if (e.changed?.find((c) => c.name === 'login_time')) {
        window.location.href = `https://${COMMON_URLS.authFe}/login`;
      }
    };

    if (cookieStore) {
      cookieStore?.addEventListener('change', onCookieChange);
    }
    return (): void => {
      if (cookieStore) {
        cookieStore.removeEventListener('change', onCookieChange);
      }
    };
  }, []);

  const themeContextValue = useMemo(() => {
    return createThemeContext(domainHeader.headerStyleId);
  }, [domainHeader.headerStyleId]);

  async function refreshTokenAsync(): Promise<void> {
    try {
      await refreshToken();
    } catch (error) {
      window.location.href = `https://${COMMON_URLS.authFe}/login`;
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      void refreshTokenAsync();
    }, 300000); // 300 000 ms - 5 mins

    return (): void => clearInterval(interval);
  }, []);

  useEffect(() => {
    void getUserData();
  }, []);

  const isBootstrapping = userLoading || convertedUserRoles.length === 0;

  const renderContent = (): JSX.Element => {
    if (isBootstrapping) {
      return (
        <SpinnerWrapperStyled>
          <Spinner />
        </SpinnerWrapperStyled>
      );
    }

    return eeRights(convertedUserRoles) ? <EmployeeApp /> : <BrokerApp />;
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {renderContent()}
      <ToastContainer />
      <IMToastManager />
    </ThemeContext.Provider>
  );
});

export default App;
